import { imageSizes, imageSrcSet, imageUrl } from "@components/componentUtils"
import { CareerDetailPage } from "@components/pages/CareerDetailPage"
import { SiteShell } from "@components/SiteShell"
import { careerDetailPageQueryOptions } from "@data/publicPageDataQuery"
import { createFileRoute, redirect } from "@tanstack/react-router"
import { isSupportedLocale } from "@utils/locale"
import { localizedRouteHead } from "@utils/routeHead"
import { pageLabels } from "@utils/routeLabels"

export const Route = createFileRoute("/$locale/careers/$slug")({
  beforeLoad: ({ params }) => {
    if (!isSupportedLocale(params.locale)) throw redirect({ to: "/404" })
  },
  loader: async ({ context, params }) => {
    const locale = params.locale as "en" | "ja"
    const data = await context.queryClient.ensureQueryData(careerDetailPageQueryOptions(locale, params.slug))
    if (!data) throw redirect({ href: `/${locale}/careers` })
    return data
  },
  head: ({ params, loaderData }) => {
    const locale = params.locale as "en" | "ja"
    const position = loaderData?.position
    const head = localizedRouteHead({
      locale,
      page: "careers",
      path: `/${locale}/careers/${params.slug}`,
      title: position?.title ? `${position.title} | PLAYBRAIN Inc.` : undefined,
      description: position?.newsBlurb,
      image: position?.newsCoverImage
        ? (imageUrl(position.newsCoverImage, "careerCard") ?? undefined)
        : undefined,
      type: "article",
    })
    if (position?.newsCoverImage) {
      head.links.push({
        rel: "preload",
        as: "image",
        href: imageUrl(position.newsCoverImage, "careerCard") ?? "",
        imageSrcSet: imageSrcSet(position.newsCoverImage, "careerCard"),
        imageSizes: imageSizes("careerCard"),
        fetchPriority: "high",
      })
    }
    return head
  },
  component: RouteComponent,
})

function RouteComponent() {
  const { locale: routeLocale } = Route.useParams()
  const locale = routeLocale as "en" | "ja"
  const { position } = Route.useLoaderData()
  const labels = pageLabels(locale, "careers")
  return (
    <SiteShell
      locale={locale}
      page={labels.page}
    >
      <CareerDetailPage
        locale={locale}
        position={position}
      />
    </SiteShell>
  )
}
